import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CornerDownLeft, Search, Star, Workflow } from 'lucide-react';
import { getFlows } from '../api/flows';
import type { FlowSummary } from '../types';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { cn } from '../lib/utils';

const MAX_RESULTS = 12;

/**
 * Every whitespace-separated word of the query must occur in the flow's name, webhook alias or
 * id — so "pers att" finds "personio/attendance-event". Lower is better; null means no match.
 */
export function matchScore(query: string, flow: FlowSummary): number | null {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 0;
  const haystacks = [flow.name, flow.webhookAlias ?? '', flow.id].map(h => h.toLowerCase());
  let score = 0;
  for (const word of words) {
    const positions = haystacks.map(h => h.indexOf(word)).filter(i => i >= 0);
    if (positions.length === 0) return null;
    score += Math.min(...positions);
  }
  return score;
}

export function rankFlows(query: string, flows: FlowSummary[]): FlowSummary[] {
  return flows
    .map(flow => ({ flow, score: matchScore(query, flow) }))
    .filter((r): r is { flow: FlowSummary; score: number } => r.score !== null)
    .sort((a, b) =>
      Number(b.flow.isFavorite) - Number(a.flow.isFavorite)
      || a.score - b.score
      || a.flow.name.localeCompare(b.flow.name))
    .slice(0, MAX_RESULTS)
    .map(r => r.flow);
}

/** Cmd/Ctrl+K from anywhere: type part of a flow's name, alias or id and press Enter to open it. */
export function FlowQuickSwitcher({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const { data: flows = [] } = useQuery({ queryKey: ['flows'], queryFn: getFlows, enabled: open });

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const results = useMemo(() => rankFlows(query, flows), [query, flows]);

  useEffect(() => setActive(0), [query]);

  function openFlow(flow: FlowSummary | undefined) {
    if (!flow) return;
    onOpenChange(false);
    navigate(`/flows/${flow.id}`);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[20%] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-lg [&>button]:hidden">
        <DialogTitle className="sr-only">Jump to flow</DialogTitle>
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive(i => Math.min(i + 1, results.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive(i => Math.max(i - 1, 0));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                openFlow(results[active]);
              }
            }}
            placeholder="Jump to flow by name, alias or id…"
            aria-label="Jump to flow"
            className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <kbd className="rounded border border-border px-1.5 text-[10px] text-muted-foreground">Esc</kbd>
        </div>
        <ul role="listbox" aria-label="Flows" className="max-h-80 overflow-y-auto p-1">
          {results.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">No flow matches</li>
          )}
          {results.map((flow, i) => (
            <li
              key={flow.id}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => openFlow(flow)}
              className={cn(
                'flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm',
                i === active ? 'bg-accent text-accent-foreground' : 'text-foreground',
              )}
            >
              {flow.isFavorite
                ? <Star className="h-4 w-4 shrink-0 text-warning" fill="currentColor" />
                : <Workflow className="h-4 w-4 shrink-0 text-muted-foreground" />}
              <span className="truncate">{flow.name}</span>
              {flow.webhookAlias && (
                <span className="truncate font-mono text-xs text-muted-foreground">/{flow.webhookAlias}</span>
              )}
              {!flow.isActive && <span className="text-xs text-muted-foreground">inactive</span>}
              {i === active && <CornerDownLeft className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
            </li>
          ))}
        </ul>
        <div className="border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground">
          ↑↓ to select · Enter to open · favorites first
        </div>
      </DialogContent>
    </Dialog>
  );
}
